/**
 * 表单校验规则生成器
 * 用于统一生成各种表单字段的校验规则
 */

import {
  safeTrim,
  checkSoftwareSuffix,
  startsWithNonAlphanumericSymbol
} from './formValidators';

/**
 * 创建软件名称校验规则
 * @param {Object} softForm - 软件表单对象
 * @param {Object} refs - 响应式引用对象 {showSoftwareNameWarning, showApp, softwareNameErr}
 * @param {Object} formRef - 表单引用
 * @returns {Array} - Element Plus 校验规则数组
 */
export const createSoftwareNameRules = (softForm, refs, formRef) => {
  return [
    {
      required: true,
      validator: (rule, value, callback) => {
        const { showSoftwareNameWarning, showApp, softwareNameErr } = refs;

        // 重置状态的辅助函数
        const resetState = () => {
          showSoftwareNameWarning.value = false;
          showApp.value = 0;
          softwareNameErr.value = true;
        };

        // 1. 检查是否为空
        if (!value.trim().length) {
          resetState();
          return callback(new Error("软件全称不能为空"));
        }

        // 2. 检查最小长度
        if (value.trim().length < 4) {
          resetState();
          return callback(new Error("软件全称过短，不能低于3个字符"));
        }

        // 3. 检查是否以特殊符号开头
        if (startsWithNonAlphanumericSymbol(value.toString().trim())) {
          resetState();
          return callback(new Error("软件全称不能以特殊符号开头"));
        }

        // 4. 检查后缀规范（根据软件类型）
        const trimmedValue = value.toString().trim();
        const type = softForm.type;

        if ((type === 0 || type === 4) && !checkSoftwareSuffix(trimmedValue, type)) {
          resetState();
          return callback(new Error('软件全称必须以软件、平台、系统结尾'));
        }

        if ((type === 1 || type === 2) && !checkSoftwareSuffix(trimmedValue, type)) {
          resetState();
          return callback(new Error('软件全称必须以软件、平台、系统、APP、客户端结尾'));
        }

        if (type === 3 && !checkSoftwareSuffix(trimmedValue, type)) {
          resetState();
          return callback(new Error('软件全称必须以软件、平台、系统、小程序结尾'));
        }

        // 所有检查通过
        softwareNameErr.value = false;

        // 如果品牌名称已填写，触发品牌名称校验
        if (softForm.brand.trim()) {
          formRef.value?.validateField("brand");
        }

        callback();
      },
      trigger: "blur"
    }
  ];
};

/**
 * 创建品牌名称校验规则
 * @param {Object} softForm - 软件表单对象
 * @returns {Array} - Element Plus 校验规则数组
 */
export const createBrandNameRules = (softForm) => {
  return [
    {
      validator: (rule, value, callback) => {
        // 检查软件名称是否包含品牌名称（不区分大小写）
        const isContain = softForm.softwareName
          ?.trim()
          .toLowerCase()
          .includes(value.trim().toLowerCase());

        if (isContain) {
          callback();
        } else {
          callback(new Error(`软件名称必须包含品牌名称：${value}`));
        }
      },
      trigger: ['blur', 'change']
    }
  ];
};

/**
 * 创建软件简称校验规则
 * @param {Object} softForm - 软件表单对象
 * @returns {Array} - Element Plus 校验规则数组
 */
export const createShortNameRules = (softForm) => {
  return [
    {
      required: false,
      validator: (rule, value, callback) => {
        const fullName = softForm.softwareName.trim();
        const shortName = value.trim();

        // 如果软件全称未填写，跳过校验
        if (!fullName.length) {
          return callback();
        }

        // 检查：简称不能与全称一致
        if (fullName === shortName) {
          return callback(new Error("软件简称不能与软件全称一致"));
        }

        // 检查：简称长度不能超过全称
        if (fullName.length < shortName.length) {
          return callback(new Error("软件简称长度不能超过软件全称"));
        }

        callback();
      },
      trigger: "blur"
    }
  ];
};

/**
 * 创建版本号校验规则
 * @returns {Array} - Element Plus 校验规则数组
 */
export const createVersionRules = () => {
  return [
    {
      required: true,
      validator: (rule, value, callback) => {
        if (!value.trim().length) {
          return callback(new Error("版本号不能为空"));
        }

        // 支持两种格式：
        // 1. 数字格式：1.0、1.0.0、1.0.0.0
        // 2. V开头格式：V1.0、V1.0.0
        const numericRegex = /^[+]?(0|([1-9]\d*))(\.\d+)(\.\d+)?$/;
        const vPrefixRegex = /^V\d+(\.\d+)*$/;

        if (numericRegex.test(value) || vPrefixRegex.test(value)) {
          callback();
        } else {
          callback(new Error("输入无效，请输入正确版本号如V1.0（大写的V）"));
        }
      },
      trigger: "blur"
    }
  ];
};

/**
 * 创建著作权人校验规则
 * @param {number} index - 著作权人索引
 * @param {Object} softForm - 软件表单对象
 * @param {Function} resetWarningCallback - 重置警告的回调函数
 * @returns {Array} - Element Plus 校验规则数组
 */
export const createApplicantRules = (index, softForm, resetWarningCallback) => {
  return [
    {
      required: true,
      validator: (rule, value, callback) => {
        // 检查：名称不能为空
        if (!safeTrim(value)) {
          return callback(new Error('著作权人不能为空'));
        }

        // 检查：名称不能重复
        const duplicateCount = softForm.applicants.filter(
          a => safeTrim(a.name) === safeTrim(value)
        ).length;

        if (duplicateCount > 1) {
          // 重置该索引的警告状态
          if (resetWarningCallback) {
            resetWarningCallback(index);
          }
          return callback(new Error('著作权人不能重复'));
        }

        callback();
      },
      trigger: "blur"
    }
  ];
};

/**
 * 验证整个表单的软件基本信息
 * @param {Object} softForm - 软件表单对象
 * @param {Function} checkSoftwareSuffix - 检查后缀的函数
 * @param {Function} startsWithNonAlphanumericSymbol - 检查特殊符号的函数
 * @returns {Object} - { valid: boolean, errorKey: string, errorMessage: string }
 */
export const validateSoftwareBasicInfo = (
  softForm,
  checkSoftwareSuffix,
  startsWithNonAlphanumericSymbol
) => {
  // 遍历表单字段进行验证
  for (const key in softForm) {
    // 使用 Object.prototype.hasOwnProperty 更安全
    if (!Object.prototype.hasOwnProperty.call(softForm, key)) continue;

    const value = softForm[key];

    // 跳过非必填字段和数组字段
    if (key === 'shortName' ||
        key === 'brand' ||
        key === 'applicant' ||
        key === 'applicants' ||
        key === 'programLanguage' ||
        Array.isArray(value)) {
      continue;
    }

    // 检查必填字段是否为空
    if (value === "") {
      return {
        valid: false,
        errorKey: key,
        errorMessage: `${getFieldLabel(key)}不能为空`
      };
    }

    // 软件名称特殊校验
    if (key === 'softwareName') {
      const trimmedValue = value.toString().trim();

      // 检查长度
      if (trimmedValue.length < 4) {
        return {
          valid: false,
          errorKey: key,
          errorMessage: '软件全称过短，不能低于3个字符'
        };
      }

      // 检查特殊符号开头
      if (startsWithNonAlphanumericSymbol(trimmedValue)) {
        return {
          valid: false,
          errorKey: key,
          errorMessage: '软件全称不能以特殊符号开头'
        };
      }

      // 检查后缀规范
      const type = softForm.type;
      if (!checkSoftwareSuffix(trimmedValue, type)) {
        return {
          valid: false,
          errorKey: key,
          errorMessage: getSuffixErrorMessage(type)
        };
      }
    }

    // 软件简称特殊校验
    if (key === 'shortName' && value.trim()) {
      const fullName = softForm.softwareName.trim();
      const shortName = value.trim();

      if (fullName === shortName) {
        return {
          valid: false,
          errorKey: key,
          errorMessage: '软件简称不能与软件全称一致'
        };
      }

      if (fullName.length < shortName.length) {
        return {
          valid: false,
          errorKey: key,
          errorMessage: '软件简称长度不能超过软件全称'
        };
      }
    }
  }

  return { valid: true };
};

/**
 * 验证著作权人列表
 * @param {Array} applicants - 著作权人数组
 * @returns {Object} - { valid: boolean, errorIndex: number, errorMessage: string }
 */
export const validateApplicantsInfo = (applicants) => {
  for (let i = 0; i < applicants.length; i++) {
    const name = safeTrim(applicants[i].name);

    if (name === '') {
      return {
        valid: false,
        errorIndex: i,
        errorKey: `applicants.${i}.name`,
        errorMessage: `第${i + 1}个著作权人名称不能为空`
      };
    }
  }

  return { valid: true };
};

/**
 * 获取字段的中文标签
 * @param {string} key - 字段名
 * @returns {string} - 中文标签
 */
const getFieldLabel = (key) => {
  const labels = {
    softwareName: '软件全称',
    shortName: '软件简称',
    version: '版本号',
    brand: '品牌名称',
    type: '软件类型',
    applyType: '申请类型',
    developMode: '开发方式'
  };

  return labels[key] || key;
};

/**
 * 根据软件类型获取后缀错误提示
 * @param {number} type - 软件类型
 * @returns {string} - 错误提示
 */
const getSuffixErrorMessage = (type) => {
  if (type === 0 || type === 4) {
    return '软件全称必须以软件、平台、系统结尾';
  }
  if (type === 1 || type === 2) {
    return '软件全称必须以软件、平台、系统、APP、客户端结尾';
  }
  if (type === 3) {
    return '软件全称必须以软件、平台、系统、小程序结尾';
  }
  return '软件名称格式不正确';
};
